import type { Quaternion } from '../types';

export type Vector3 = readonly [number, number, number];

const IDENTITY_QUATERNION: Quaternion = [1, 0, 0, 0];

export const multiplyQuaternions = (
  [aw, ax, ay, az]: Quaternion,
  [bw, bx, by, bz]: Quaternion
): Quaternion => [
  aw * bw - ax * bx - ay * by - az * bz,
  aw * bx + ax * bw + ay * bz - az * by,
  aw * by - ax * bz + ay * bw + az * bx,
  aw * bz + ax * by - ay * bx + az * bw,
];

export const conjugateQuaternion = ([w, x, y, z]: Quaternion): Quaternion => [
  w,
  -x,
  -y,
  -z,
];

export const normalizeQuaternion = (quaternion: Quaternion): Quaternion => {
  const length = Math.hypot(...quaternion);
  if (length === 0) return IDENTITY_QUATERNION;
  const [w, x, y, z] = quaternion;
  return [w / length, x / length, y / length, z / length];
};

/** `axis` need not be unit length; a zero axis is no rotation. */
export const quaternionFromAxisAngle = (
  [x, y, z]: Vector3,
  angle: number
): Quaternion => {
  const length = Math.hypot(x, y, z);
  if (length === 0 || angle === 0) return IDENTITY_QUATERNION;
  const scale = Math.sin(angle / 2) / length;
  return [Math.cos(angle / 2), x * scale, y * scale, z * scale];
};

export const rotateVector = (
  quaternion: Quaternion,
  [x, y, z]: Vector3
): Vector3 => {
  const [, rx, ry, rz] = multiplyQuaternions(
    multiplyQuaternions(quaternion, [0, x, y, z]),
    conjugateQuaternion(quaternion)
  );
  return [rx, ry, rz];
};

/**
 * The rotation carrying the unit x, y and z axes onto three orthonormal vectors.
 *
 * Shepperd's method: it divides by the largest of the four candidate terms, so it stays accurate
 * at every orientation rather than losing precision near a half turn.
 */
export const quaternionFromAxes = (
  [m00, m10, m20]: Vector3,
  [m01, m11, m21]: Vector3,
  [m02, m12, m22]: Vector3
): Quaternion => {
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2;
    return normalizeQuaternion([
      s / 4,
      (m21 - m12) / s,
      (m02 - m20) / s,
      (m10 - m01) / s,
    ]);
  }
  if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    return normalizeQuaternion([
      (m21 - m12) / s,
      s / 4,
      (m01 + m10) / s,
      (m02 + m20) / s,
    ]);
  }
  if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    return normalizeQuaternion([
      (m02 - m20) / s,
      (m01 + m10) / s,
      s / 4,
      (m12 + m21) / s,
    ]);
  }
  const s = Math.sqrt(1 + m22 - m00 - m11) * 2;
  return normalizeQuaternion([
    (m10 - m01) / s,
    (m02 + m20) / s,
    (m12 + m21) / s,
    s / 4,
  ]);
};
